import { useEffect, useState, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowsClockwise,
  ChartLineUp,
  Car,
  Briefcase,
  Wallet,
  Plus,
  Trash,
  Coins,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDate } from "@/lib/fmt";

/**
 * Global Driver Dashboard — consolidated business view.
 *
 * Shows a single TOTAL revenue KPI, then breaks it down into the three
 * recognised sources (dispatch rides, personal rides, external revenue)
 * with their respective counts. Strictly driver-owned data: the backend
 * scopes every query by the caller's id.
 *
 * The driver can add / delete external revenue entries here. Everything
 * else (dispatch + personal rides) is read-only from this page — those
 * flows are edited elsewhere in the app.
 *
 * Priority 1 · v1.0 — validated and ready for production rollout.
 */
const PERIODS = [
  { code: "today", label: "Aujourd'hui" },
  { code: "week",  label: "Cette semaine" },
  { code: "month", label: "Ce mois" },
  { code: "custom", label: "Personnalisé" },
];

function fmtEur(n) {
  const v = Number(n || 0);
  return `${v.toFixed(2).replace(/\.00$/, "")} €`;
}

function todayIso() {
  const d = new Date();
  const pad = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function GlobalDriverDashboard() {
  const [period, setPeriod] = useState("month");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ amount: "", date: todayIso(), source: "", note: "" });
  const [submitting, setSubmitting] = useState(false);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period };
      if (period === "custom") {
        if (customStart) params.start = customStart;
        if (customEnd) params.end = customEnd;
      }
      const { data } = await api.get("/driver/dashboard", { params });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de charger le tableau de bord");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [period, customStart, customEnd]);

  useEffect(() => {
    // Auto-fetch on period change except when custom without both bounds.
    if (period === "custom" && (!customStart || !customEnd)) return;
    fetchDashboard();
  }, [period, customStart, customEnd, fetchDashboard]);

  const totals = data?.breakdown || { dispatch: 0, personal: 0, external: 0 };
  const counts = data?.counts   || { dispatchRides: 0, personalRides: 0, externalEntries: 0 };
  const entries = data?.externalRevenueEntries || [];
  const total  = data?.totalRevenue ?? 0;

  const periodLabel = useMemo(() => {
    if (!data?.period) return "";
    const { start, end, type } = data.period;
    const name = PERIODS.find((p) => p.code === type)?.label || type;
    return `${name} · ${fmtDate(start)} → ${fmtDate(end)}`;
  }, [data]);

  const handleCreate = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) {
      toast.error("Montant invalide");
      return;
    }
    if (!form.date) {
      toast.error("Date requise");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/driver/external-revenues", {
        amount: amt,
        date: form.date,
        source: form.source || null,
        note: form.note || null,
      });
      toast.success("Revenu externe ajouté");
      setModalOpen(false);
      setForm({ amount: "", date: todayIso(), source: "", note: "" });
      fetchDashboard();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'ajout");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm("Supprimer cette entrée de revenu externe ?")) return;
    try {
      await api.delete(`/driver/external-revenues/${entryId}`);
      toast.success("Entrée supprimée");
      fetchDashboard();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de la suppression");
    }
  };

  return (
    <div data-testid="global-driver-dashboard" className="space-y-4">
      {/* ─── Header: period selector + refresh ───────────────── */}
      <div className="bg-[#0f0f0f] border border-zinc-800 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <ChartLineUp size={20} className="text-amber-400 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-white">Tableau de bord global</p>
              <p className="text-[11px] text-zinc-500 italic truncate">{periodLabel || "Sélectionnez une période"}</p>
            </div>
          </div>
          <Button
            data-testid="global-dashboard-refresh"
            onClick={fetchDashboard}
            disabled={loading}
            variant="outline"
            size="sm"
            className="h-8 px-2 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} />
          </Button>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {PERIODS.map((p) => (
            <button
              key={p.code}
              data-testid={`global-dashboard-period-${p.code}`}
              onClick={() => setPeriod(p.code)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                period === p.code
                  ? "bg-amber-500 text-black border-amber-500 font-semibold"
                  : "bg-zinc-900 text-zinc-300 border-zinc-700 hover:bg-zinc-800"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {period === "custom" && (
          <div className="grid grid-cols-2 gap-2 pt-1">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Début</Label>
              <Input
                data-testid="global-dashboard-custom-start"
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="bg-[#1A1A1A] border-zinc-700 text-white h-9"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Fin</Label>
              <Input
                data-testid="global-dashboard-custom-end"
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="bg-[#1A1A1A] border-zinc-700 text-white h-9"
              />
            </div>
          </div>
        )}
      </div>

      {/* ─── Main KPI: total revenue ─────────────────────────── */}
      <div className="bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent border border-amber-500/30 rounded-lg p-5">
        <div className="flex items-center gap-2 mb-1">
          <Wallet size={16} className="text-amber-400" />
          <p className="text-[11px] uppercase tracking-wider text-amber-400/80 font-semibold">Revenu total</p>
        </div>
        <p data-testid="global-dashboard-total" className="text-4xl font-bold text-white tracking-tight">
          {fmtEur(total)}
        </p>
        <p className="text-[11px] text-zinc-500 mt-2">
          Dispatch + Courses personnelles + Revenus externes
        </p>
      </div>

      {/* ─── Breakdown: 3 cards ──────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div data-testid="global-dashboard-dispatch" className="bg-[#121212] border border-emerald-500/25 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <Car size={14} className="text-emerald-400" />
              <p className="text-[11px] uppercase tracking-wider text-zinc-400 font-medium">Dispatch</p>
            </div>
            <span className="text-[10px] text-zinc-500">
              {counts.dispatchRides} course{counts.dispatchRides > 1 ? "s" : ""}
            </span>
          </div>
          <p className="text-2xl font-semibold text-emerald-300">{fmtEur(totals.dispatch)}</p>
          <p className="text-[10px] text-zinc-600 mt-1.5">Courses terminées (net chauffeur)</p>
        </div>
        <div data-testid="global-dashboard-personal" className="bg-[#121212] border border-sky-500/25 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <Briefcase size={14} className="text-sky-400" />
              <p className="text-[11px] uppercase tracking-wider text-zinc-400 font-medium">Personnel</p>
            </div>
            <span className="text-[10px] text-zinc-500">
              {counts.personalRides} course{counts.personalRides > 1 ? "s" : ""}
            </span>
          </div>
          <p className="text-2xl font-semibold text-sky-300">{fmtEur(totals.personal)}</p>
          <p className="text-[10px] text-zinc-600 mt-1.5">Courses privées terminées</p>
        </div>
        <div data-testid="global-dashboard-external" className="bg-[#121212] border border-fuchsia-500/25 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <Coins size={14} className="text-fuchsia-400" />
              <p className="text-[11px] uppercase tracking-wider text-zinc-400 font-medium">Externe</p>
            </div>
            <span className="text-[10px] text-zinc-500">
              {counts.externalEntries} entrée{counts.externalEntries > 1 ? "s" : ""}
            </span>
          </div>
          <p className="text-2xl font-semibold text-fuchsia-300">{fmtEur(totals.external)}</p>
          <p className="text-[10px] text-zinc-600 mt-1.5">Uber, Bolt, clients directs…</p>
        </div>
      </div>

      {/* ─── External revenue section ─────────────────────────── */}
      <div className="bg-[#0f0f0f] border border-zinc-800 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-medium text-white">Revenus externes</p>
            <p className="text-[11px] text-zinc-500">
              Revenus perçus hors plateforme — n'interfèrent avec aucune course ni facture.
            </p>
          </div>
          <Button
            data-testid="global-dashboard-add-external"
            onClick={() => setModalOpen(true)}
            className="bg-amber-500 text-black hover:bg-amber-600 font-semibold h-9 shrink-0"
          >
            <Plus size={16} className="mr-1" /> Ajouter
          </Button>
        </div>
        {entries.length === 0 ? (
          <div data-testid="global-dashboard-external-empty" className="text-center py-8 text-zinc-500 text-xs">
            Aucun revenu externe sur cette période
          </div>
        ) : (
          <div className="space-y-1.5">
            {entries.map((e) => (
              <div
                key={e.id}
                data-testid={`external-entry-${e.id}`}
                className="flex items-center justify-between gap-2 bg-[#121212] border border-zinc-800 rounded-md px-3 py-2.5"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-fuchsia-300">{fmtEur(e.amount)}</span>
                    <span className="text-[11px] text-zinc-500">{fmtDate(e.date)}</span>
                    {e.source && (
                      <span className="text-[10px] uppercase tracking-wider bg-zinc-800 text-zinc-300 border border-zinc-700 rounded px-1.5 py-0.5">
                        {e.source}
                      </span>
                    )}
                  </div>
                  {e.note && <p className="text-[11px] text-zinc-400 mt-0.5 truncate">{e.note}</p>}
                </div>
                <button
                  data-testid={`external-entry-delete-${e.id}`}
                  onClick={() => handleDelete(e.id)}
                  className="text-zinc-500 hover:text-red-400 p-1.5 rounded transition-colors"
                  title="Supprimer"
                >
                  <Trash size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ─── Add external revenue modal ───────────────────────── */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Ajouter un revenu externe</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              Saisissez un revenu perçu hors plateforme (Uber, Bolt, client direct en espèces, etc.).
              Aucune facture ni course ne sera créée.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-400">Montant (€) *</Label>
              <Input
                data-testid="external-form-amount"
                type="number"
                step="0.01"
                min="0.01"
                value={form.amount}
                onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
                placeholder="Ex. 42.50"
                className="bg-[#1A1A1A] border-zinc-700 text-white h-10"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-400">Date *</Label>
              <Input
                data-testid="external-form-date"
                type="date"
                value={form.date}
                onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
                className="bg-[#1A1A1A] border-zinc-700 text-white h-10"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-400">Source (optionnel)</Label>
              <Input
                data-testid="external-form-source"
                value={form.source}
                onChange={(e) => setForm((p) => ({ ...p, source: e.target.value }))}
                placeholder="Uber, Bolt, Client direct…"
                className="bg-[#1A1A1A] border-zinc-700 text-white h-10"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-400">Note (optionnel)</Label>
              <Input
                data-testid="external-form-note"
                value={form.note}
                onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
                placeholder="Détail libre"
                className="bg-[#1A1A1A] border-zinc-700 text-white h-10"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              onClick={() => setModalOpen(false)}
              variant="outline"
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              Annuler
            </Button>
            <Button
              data-testid="external-form-submit"
              onClick={handleCreate}
              disabled={submitting}
              className="bg-amber-500 text-black hover:bg-amber-600 font-semibold"
            >
              {submitting ? "Ajout…" : "Ajouter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
